import { Component, Inject, OnInit, ViewChild } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { HttpLoaderService, SnackbarService, SearchDataRequest } from '@tymes4-shared';
import { TranslateService } from "@ngx-translate/core";
import { AssignmentHistoryViewPagedResult, AssignmentService, EventPlacementService, HelpcenterTicketView, IdentificationStateEnum, MailTemplateService, OrderService, PrintableTicket, SalesChannelService, SendMailETicketArgs, TicketHistoryCurrentView, TicketPrintArgs, TicketReIssueArgs, TicketService, TicketSwapStatusEnum, TicketSwapTypeEnum, TicketView } from "../../../api";
import { ApplicationModuleHelperService } from '../../../services/application-module.service';
import { EditEventPlacementPolicyComponent } from "../../../dialogs/edit-event-placement-policy/edit-event-placement-policy.component";
import { Router } from "@angular/router";
import { TicketDownloadArgs } from "../../../api/model/ticketDownloadArgs";
import { saveAs } from 'file-saver';
import { ManualMailDialogComponent, ManualMailDialogParameters, ManualMailDialogResult } from "../../../dialogs/manual-mail-dialog/manual-mail-dialog.component";

@Component({
    selector: 'app-ticket-assignment-history',
    templateUrl: './ticket-assignment-history.component.html',
    styleUrls: ['./ticket-assignment-history.component.scss'],
    standalone: false
})
export class TicketAssignmentHistoryComponent implements OnInit {

  @ViewChild('listVw') listVw: any;

  public listOptions = {
    search: false,
    showBarcodeSearch: false,
    pageSize: 6,
    fixedTableLayout: true,
    disableSort: true,
    headers: [
      { sortField: 'Created', label: 'RELATIONS.CUSTOMERCARD.TICKET-ASSIGNMENT-HISTORY.LISTVIEW.HEADER.DATE', isDefaultSort: 'desc', width: 90 },
      { label: 'RELATIONS.CUSTOMERCARD.TICKET-ASSIGNMENT-HISTORY.LISTVIEW.HEADER.TIME', width: 60 },
      { sortField: 'RELATIONS.CUSTOMERCARD.TICKET-ASSIGNMENT-HISTORY.LISTVIEW.HEADER.STATUS', label: 'ACTION / STATUS', width: 150 },
      { sortField: 'By', label: 'RELATIONS.CUSTOMERCARD.TICKET-ASSIGNMENT-HISTORY.LISTVIEW.HEADER.BY', width: 180 },
      { sortField: 'To', label: 'RELATIONS.CUSTOMERCARD.TICKET-ASSIGNMENT-HISTORY.LISTVIEW.HEADER.TO', width: 180 },
      { sortField: 'Barcode', label: 'RELATIONS.CUSTOMERCARD.TICKET-ASSIGNMENT-HISTORY.LISTVIEW.HEADER.BARCODE', width: 200 }
    ]
  };

  public mostRecentItem: TicketHistoryCurrentView = {};
  public eventPlacementId: number;
  public customerId: number;
  public identificationStatus: string = '';
  public TicketSwapStatusEnum = TicketSwapStatusEnum;
  public isIbaActive: boolean = false;
  public busy: boolean = false;
  private hasChanges: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public passedData: { eventPlacementId: number, customerId: number },
    public dialogRef: MatDialogRef<TicketAssignmentHistoryComponent>,
    private assignmentService: AssignmentService,
    private eventPlacementService: EventPlacementService,
    private ticketService: TicketService,
    private dialog: MatDialog,
    private snackbar: SnackbarService,
    private translate: TranslateService,
    private applicationModuleHelperService: ApplicationModuleHelperService,
    private loader: HttpLoaderService,
    private router: Router,
    private salesChannelService: SalesChannelService,
    private orderService: OrderService,
    private mailTemplateService: MailTemplateService,
  ) {
    this.eventPlacementId = passedData.eventPlacementId;
    this.customerId = passedData.customerId;
  }

  ngOnInit(): void {
    this.refreshLatestTicketData();

    this.applicationModuleHelperService.getActiveModules().subscribe((activeModules) => {
      if (activeModules !== null) {
        this.isIbaActive = activeModules.some((module) => module.toUpperCase() == 'IBA')
      }
    })
  }

  OnDataRequest(e: SearchDataRequest) {
    this.loader.open();
    this.assignmentService.searchAssigments(this.customerId, this.eventPlacementId, e.searchText, e.pageNr, e.pageSize, e.sortField, e.sortAsc, null).subscribe((resp: AssignmentHistoryViewPagedResult) => {
      this.listVw.Data = resp;
      this.loader.close();
    }, _error => {
      this.loader.close();
    });
  }

  refreshLatestTicketData() {
    this.loader.open();
    this.assignmentService.getLatestAssignmentHistory(this.eventPlacementId, this.customerId).subscribe((resp: TicketHistoryCurrentView) => {
      this.loader.close();
      this.mostRecentItem = resp;
    }, _error => {
      this.loader.close();
    });
  }

  canCreateDuplicate(): boolean {
    return !!this.mostRecentItem && !this.hasPendingStatus(this.mostRecentItem.Status);
  }

  private hasPendingStatus(status: TicketSwapStatusEnum | undefined): boolean {
    return status === TicketSwapStatusEnum.PendingInvitation || status === TicketSwapStatusEnum.Offered;
  }

  openEditPolicy() {
    if (!this.isIbaActive) {
      return;
    }

    this.busy = true;
    this.eventPlacementService.getTicketsByEventPlacementIds([this.eventPlacementId]).subscribe((tickets: TicketView[]) => {
      this.busy = false;
      if (!tickets || tickets.length === 0) {
        return;
      }

      const dialogRef = this.dialog.open(EditEventPlacementPolicyComponent, {
        width: '900px',
        height: '800px',
        disableClose: false,
        data: {
          title: this.translate.instant('ADMINISTRATION.TICKETS.DIALOG.TITLE.EDIT-TICKETS', { ticketAmmount: tickets.length }),
          tickets
        }
      });

      dialogRef.afterClosed().subscribe((reload: boolean) => {
        if (!reload) {
          return;
        }

        this.hasChanges = true;
        this.refreshLatestTicketData();
        if (this.listVw) {
          this.listVw.refreshData();
        }
      });
    }, _error => {
      this.busy = false;
    });
  }

  onReplaceButtonClick() {
    if (!this.canCreateDuplicate()) {
      this.snackbar.open(this.translate.instant('RELATIONS.TICKET-ASSIGNMENT-HISTORY-DIALOG.DUPLICATE.ERROR.STATUS'), 'GENERIC.SNACKBAR.CLOSE');
      return;
    }

    this.busy = true;
    this.loader.open();
    const args: TicketReIssueArgs = {
      EventPlacementIds: [this.eventPlacementId]
    };

    this.ticketService.ticketReIssue(args).subscribe(() => {
      this.loader.close();
      this.busy = false;
      this.hasChanges = true;
      this.snackbar.open(this.translate.instant('RELATIONS.TICKET-ASSIGNMENT-HISTORY-DIALOG.DUPLICATE.SUCCESS'), 'GENERIC.SNACKBAR.CLOSE');
      this.refreshLatestTicketData();
      if (this.listVw) {
        this.listVw.refreshData();
      }
    }, _error => {
      this.loader.close();
      this.busy = false;
    });
  }

  openUpgrade() {
    this.snackbar.open(this.translate.instant('RELATIONS.TICKET-ASSIGNMENT-HISTORY-DIALOG.UPGRADE.NOT-AVAILABLE'), 'GENERIC.SNACKBAR.CLOSE');
  }

  closeDialog() {
    this.dialogRef.close(this.hasChanges);
  }

  constructCustomerIdentificationModel(isTicketHolder: boolean) {
    if (this.mostRecentItem) {
      return {
        CalculatedName: isTicketHolder ? this.mostRecentItem.TicketHolderName : this.mostRecentItem.BoughtByName,
        IsIdentified: isTicketHolder ? this.mostRecentItem.TicketHolderIsIdentified : this.mostRecentItem.BoughtByIsIdentified,
        CustomerNumber: isTicketHolder ? this.mostRecentItem.TicketHolderNumber : this.mostRecentItem.BoughtByNumber,
        CustomerId: isTicketHolder ? this.mostRecentItem.TicketHolderId : this.mostRecentItem.BoughtById,
      }
    }
  }

  constructSeondaryCustomerIdentificationModel() {
    if (this.mostRecentItem) {
      return {
        CalculatedName: this.mostRecentItem.SecondaryBoughtByName,
        IsIdentified: this.mostRecentItem.SecondaryBoughtByIsIdentified,
        CustomerNumber:  this.mostRecentItem.SecondaryBoughtByNumber,
        CustomerId:  this.mostRecentItem.SecondaryBoughtById,
      }
    }
  }

  getIdentificationIconClass(isTicketHolder: boolean): string {
    if (!this.mostRecentItem) {
      return '';
    }

    let statusToCheck: IdentificationStateEnum = isTicketHolder ? this.mostRecentItem.TicketHolderIdentificationStatus : this.mostRecentItem.BoughtByIdentificationStatus;

    switch (statusToCheck) {
      case IdentificationStateEnum.NotStarted:
        return 'fas fa-file-upload text-warning pe-2';
      case IdentificationStateEnum.PendingCheck:
        return 'fas fa-clock text-warning pe-2';
      case IdentificationStateEnum.BeingChecked:
        return 'fas fa-clock text-warning pe-2';
      case IdentificationStateEnum.Verified:
        return 'fas fa-check-circle text-success pe-2';
      case IdentificationStateEnum.Rejected:
        return 'fas fa-exclamation-circle text-danger pe-2';
    }
  }

  getIdentificationTooltip(isTicketHolder: boolean): string {
    if (!this.mostRecentItem) {
      return '';
    }

    const statusToCheck: IdentificationStateEnum = isTicketHolder ? this.mostRecentItem.TicketHolderIdentificationStatus : this.mostRecentItem.BoughtByIdentificationStatus;
    return this.translate.instant(this.getIdentificationStatusTranslationKey(statusToCheck));
  }

  getSecondaryIdentificationTooltip(): string {
    if (!this.mostRecentItem) {
      return '';
    }

    return this.translate.instant(this.getIdentificationStatusTranslationKey(this.mostRecentItem.SecondaryBoughtByIdentificationStatus));
  }

  private getIdentificationStatusTranslationKey(status: IdentificationStateEnum): string {
    switch (status) {
      case IdentificationStateEnum.NotStarted:
        return 'RELATIONS.TICKET-ASSIGNMENT-HISTORY-DIALOG.IDENTIFICATION-STATUS.NOT-STARTED';
      case IdentificationStateEnum.PendingCheck:
        return 'RELATIONS.TICKET-ASSIGNMENT-HISTORY-DIALOG.IDENTIFICATION-STATUS.PENDING-CHECK';
      case IdentificationStateEnum.BeingChecked:
        return 'RELATIONS.TICKET-ASSIGNMENT-HISTORY-DIALOG.IDENTIFICATION-STATUS.BEING-CHECKED';
      case IdentificationStateEnum.Verified:
        return 'RELATIONS.TICKET-ASSIGNMENT-HISTORY-DIALOG.IDENTIFICATION-STATUS.VERIFIED';
      case IdentificationStateEnum.Rejected:
        return 'RELATIONS.TICKET-ASSIGNMENT-HISTORY-DIALOG.IDENTIFICATION-STATUS.REJECTED';
      default:
        return '';
    }
  }

  getStatusValue(type: TicketSwapTypeEnum, status: TicketSwapStatusEnum) {
    if (type === TicketSwapTypeEnum.Duplicate) {
      return "RELATIONS.TICKET-ASSIGNMENT-HISTORY-DIALOG.STATUS.RE-ISSUED";
    }
    
    switch (status) {
      case TicketSwapStatusEnum.None:
        return "RELATIONS.TICKET-ASSIGNMENT-HISTORY-DIALOG.STATUS.NONE";
      case TicketSwapStatusEnum.Offered:
        return "RELATIONS.TICKET-ASSIGNMENT-HISTORY-DIALOG.STATUS.OFFERED";
      case TicketSwapStatusEnum.Accepted:
        return "RELATIONS.TICKET-ASSIGNMENT-HISTORY-DIALOG.STATUS.ACCEPTED";
      case TicketSwapStatusEnum.Sold:
        return "RELATIONS.TICKET-ASSIGNMENT-HISTORY-DIALOG.STATUS.SOLD";
      case TicketSwapStatusEnum.Withdrawn:
        return "RELATIONS.TICKET-ASSIGNMENT-HISTORY-DIALOG.STATUS.WITHDRAW";
      case TicketSwapStatusEnum.Revoked:
        return "RELATIONS.TICKET-ASSIGNMENT-HISTORY-DIALOG.STATUS.REVOKED";
      case TicketSwapStatusEnum.PendingInvitation:
        return "RELATIONS.TICKET-ASSIGNMENT-HISTORY-DIALOG.STATUS.ASSIGNMENTINVITED";
      case TicketSwapStatusEnum.InvitationDeclined:
        return "RELATIONS.TICKET-ASSIGNMENT-HISTORY-DIALOG.STATUS.DECLINED";
      case TicketSwapStatusEnum.Assigned:
        return "RELATIONS.TICKET-ASSIGNMENT-HISTORY-DIALOG.STATUS.ASSIGNED";
      case TicketSwapStatusEnum.AssignmentRequired:
        return "RELATIONS.TICKET-ASSIGNMENT-HISTORY-DIALOG.STATUS.ASSIGNMENT-REQUIRED";
      case TicketSwapStatusEnum.ReIssued:
        return "RELATIONS.TICKET-ASSIGNMENT-HISTORY-DIALOG.STATUS.RE-ISSUED";
      default:
        return "";
    }
  }

  openOrder(ev: MouseEvent, orderId: number) {
    ev.stopPropagation();
    ev.preventDefault();
    this.closeDialog();

    this.router.navigate(['/sales/orders/details', orderId]);
  }

  reissueTicket() {
    if (this.hasPendingStatus(this.mostRecentItem.Status)) {
      this.snackbar.open(this.translate.instant('HELPCENTER.TICKETS.REISSUE.ERROR.STATUS'), 'GENERIC.SNACKBAR.CLOSE');
      return;
    }
    const epIds = this.mostRecentItem.EventPlacementId ? [this.mostRecentItem.EventPlacementId] : [];
    const args: TicketReIssueArgs = {
      EventPlacementIds: epIds
    }
    return this.ticketService.ticketReIssue(args);
  }

  ticketPrintable() {
    return !this.hasPendingStatus(this.mostRecentItem.Status) && (this.mostRecentItem.Status == TicketSwapStatusEnum.Assigned || !this.mostRecentItem.AssignmentRequired);
  }

  onPrintButtonClick() {
    this.loader.open();
    this.reissueTicket()?.subscribe((result: TicketReIssueArgs) => {

      const epIds = this.mostRecentItem.EventPlacementId ? [this.mostRecentItem.EventPlacementId] : [];
      const args: TicketPrintArgs = {
        OrderId: Number(this.mostRecentItem.OrderId),
        SelectedEventPlacementIds: epIds,
        PackagingSlipOption: 1, // No packaging slip
        CutMode: 1, // Cut/advance after ticket
        isDuplicate: false,
        DuplicationReasonId: null,
      };

      this.ticketService.printTicket(args).subscribe(() => {
        this.loader.close();
        this.hasChanges = true;
        this.snackbar.open(this.translate.instant('HELPCENTER.PRINT.SUCCESS'));
        this.refreshLatestTicketData();
        if (this.listVw) {
          this.listVw.refreshData();
        }
      }, error => {
        this.loader.close();
        this.snackbar.open(this.translate.instant('HELPCENTER.PRINT.ERROR'));
      });
    }, error => {
      this.loader.close();
      this.snackbar.open(this.translate.instant('HELPCENTER.PRINT.ERROR'));
    });
  }

  onDownloadButtonClick() {
    this.loader.open();
    this.reissueTicket()?.subscribe((result: TicketReIssueArgs) => {

      const epIds = this.mostRecentItem.EventPlacementId ? [this.mostRecentItem.EventPlacementId] : [];
      const args: TicketDownloadArgs = {
        OrderId: Number(this.mostRecentItem.OrderId),
        SelectedEventPlacementIds: epIds,
        SalesChannelId: this.mostRecentItem.SalesChannelId,
        isDuplicate: false,
      };

      this.ticketService.downloadTicket(args).subscribe((file: Blob) => {
        const guid = crypto.randomUUID();
        saveAs(file, `E-tickets_reprint${guid}.zip`);
        this.loader.close();
        this.hasChanges = true;
        this.snackbar.open(this.translate.instant('HELPCENTER.PRINT.SUCCESS'));
        this.refreshLatestTicketData();
        if (this.listVw) {
          this.listVw.refreshData();
        }
      }, error => {
        this.loader.close();
        this.snackbar.open(this.translate.instant('HELPCENTER.PRINT.ERROR'));
      });
    }, error => {
      this.loader.close();
      this.snackbar.open(this.translate.instant('HELPCENTER.PRINT.ERROR'));
    });

    return false;
  }

  onEmailButtonClick() {
    this.loader.open();
    this.eventPlacementService.getTicketsByEventPlacementIds([this.eventPlacementId]).subscribe((tickets: TicketView[]) => {  
      this.reissueTicket()?.subscribe((result: TicketReIssueArgs) => {
        if (!tickets || tickets.length === 0) {
          this.loader.close();
          this.snackbar.open(this.translate.instant('HELPCENTER.PRINT.ERROR'));
          return;
        }

        const ticket = tickets[0];
        const data = <ManualMailDialogParameters>{};
        data.title = 'ORDER.DETAILS.TICKETS.SEND-TICKETS.TITLE';
        data.email = ticket?.OrderLineTicketCustomerEmail || '';
        data.showAttachments = false;

        this.salesChannelService.listAllSalesChannels().subscribe(salesChannels => {
          const filteredSalesChannels = salesChannels.filter(sc => ticket.SalesChannelId === sc.Id);

          data.attachments = this.mapToPrintableTickets([ticket]);
          data.salesChannels = filteredSalesChannels;

          this.mailTemplateService.getConfirmationMail(ticket.OrderId).subscribe((result) => {
            this.loader.close();
            data.body = result.Body ?? '';
            data.subject = result.Subject ?? '';

            const dialogRef: MatDialogRef<any> = this.dialog.open(ManualMailDialogComponent, {
              width: '720px',
              disableClose: true,
              data: data
          });

          dialogRef.afterClosed().subscribe((result: ManualMailDialogResult) => {
            if (!result) {
              return;
            }

            this.loader.open();

            const args: SendMailETicketArgs = {
              Email: result.email,
              Subject: result.subject,
              Body: result.body,
              SalesChannelId: result.salesChannelId,
              EventPlacementIds: result.attachments
            }

            this.orderService.sendETicketMail(ticket.OrderId, args).subscribe(() => {
              this.loader.close();
        
              this.refreshLatestTicketData();
              this.hasChanges = true;
              this.snackbar.open(this.translate.instant('GENERIC.SNACKBAR.EMAIL-HAS-BEEN-SENT'), 'GENERIC.SNACKBAR.CLOSE');
              this.refreshLatestTicketData();
              if (this.listVw) {
                this.listVw.refreshData();
              }
            });
          });

          return false;
        });
      }, error => {
      this.loader.close();
      this.snackbar.open(this.translate.instant('HELPCENTER.PRINT.ERROR'));
    });   
      }, error => {
      this.loader.close();
      this.snackbar.open(this.translate.instant('HELPCENTER.PRINT.ERROR'));
      });
    });
  }

  
  private mapToPrintableTickets(tickets: HelpcenterTicketView[]): PrintableTicket[] {
    return tickets.map(ticket => ({
      Id: ticket.EventPlacementId,
      EventStartDate: ticket.EventStartDateTime,
      EventName: ticket.EventName,
      EventId: ticket.EventId,
      TicketCustomerName: ticket.TicketCustomerName,
      IdentificationCustomerModel: {
        FirstName: ticket.OrderCustomerFirstname,
        MiddleName: ticket.OrderCustomerMiddlename,
        LastName: ticket.OrderCustomerLastname,
        CustomerType: ticket.CustomerType,
        CustomerName: ticket.TicketCustomerName,
        IsIdentified: ticket.TicketCustomerIsIdentified,
        CustomerNumber: ticket.TicketCustomerNumber,
        TicketCustomerId: ticket.TicketCustomerId
      },
      Row: ticket.Row,
      Seat: ticket.Seat,
      Barcode: ticket.EventPlacementBarcode ?? ticket.HardcardBarcode,
      TicketSwapStatus: ticket.TicketSwapStatus,
      Selectable: this.ticketPrintable()
    }));
  }

}